import { getCountryByCountryCode } from '@app/ducks/countries';
import { cn } from '@app/utils/cn';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Addresses } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

interface Props {
  shippingAddress: Addresses.MixedShipping;
  isSelected: boolean;
  inputId: string;
  describedbyId: string;
}

const shouldShowData = (data?: Nullable<string>) => isDefined(data) && !isEmpty(data);
const showData = (data?: Nullable<string>) => shouldShowData(data) && data;

export const ShippingAddressRadioBody = ({ shippingAddress, isSelected, inputId, describedbyId }: Props) => {
  const { t } = useTranslation(['common']);
  const country = isDefined(shippingAddress.country) ? shippingAddress.country : '';
  const shippingCountryCodeSelector = React.useMemo(() => getCountryByCountryCode(country), [country]);
  const shippingCountry = useSelector(shippingCountryCodeSelector);

  const stateName = isDefined(shippingAddress.state) ? shippingCountry.states?.[shippingAddress.state]?.name ?? '' : '';
  const shouldShowContactPersonData = shouldShowData(shippingAddress.contactPerson) || shouldShowData(shippingAddress.phoneNumber);
  const textColor = isSelected ? 'text-neutral-20' : 'text-neutral-40';

  return (
    <div data-sentry-mask className="flex flex-col gap-5">
      <label htmlFor={inputId} className="flex flex-col absolute-cursor-pointer">
        <span className={cn('truncate heading-3', textColor)}>{shippingAddress.company}</span>
        <span className="truncate text-neutral-40 body-2">{shippingAddress.email}</span>
      </label>
      <div id={describedbyId}>
        <div className="flex flex-col break-words">
          <span className={cn('body-6', textColor)}>{t('common:address')}</span>
          <span className={cn('body-5', textColor)}>
            {shippingAddress.address1} {shippingAddress.address2}
          </span>
          <span className={cn('body-5', textColor)}>
            {shippingAddress.zipCode} {shippingAddress.city}
          </span>
          <span className={cn('space-x-1 body-5', textColor)}>
            {!isEmpty(stateName) && <span>{stateName}</span>}
            <span>{shippingCountry.name}</span>
          </span>
        </div>
        {shouldShowContactPersonData && (
          <div className="flex flex-col break-words">
            <span className={cn('body-6', textColor)}>{t('common:contact_person')}</span>
            <span className={cn('body-5', textColor)}>{showData(shippingAddress.contactPerson)}</span>
            <span className={cn('body-5', textColor)}>{showData(shippingAddress.phoneNumber)}</span>
          </div>
        )}
      </div>
    </div>
  );
};
